/* eslint-disable camelcase */
import React, { useCallback, useState } from 'react';
import { IntlProvider } from 'react-intl';

import AppWithContexts from './AppWithContexts';
import Constants from './common/Constants';
import { getLanguageName } from './common/Utils';
import { LocalizationContext } from './components/localization/LocalizationContext';
import en_US_labels from './resources/en-US/text/labels';
import ja_JP_labels from './resources/ja-JP/text/labels';

/**
 * Language is "English" or "日本語". Locale is "en-US" or "ja-JP"
 */

const localizedLabels: Record<string, Record<string, string>> = {
    'en-US': en_US_labels,
    'ja-JP': ja_JP_labels,
};
const supportedLocales = Object.keys(localizedLabels);

/**
 * Checks if a locale is supported
 * @param locale the locale to check
 * @returns whether that locale has labels in A2T
 */
const isLocaleSupported = (locale: string): boolean => supportedLocales.includes(locale);

// Need to lookup locale --> language and vice versa
const localeToLanguageMap: Record<string, string> = supportedLocales.reduce((localeToLanguageMap, currentLocale) => {
    // Calling `getLanguageName(currentLocale, currentLocale)` gets the name of the language in that language. E.g. English, 日本語, Deutsch
    localeToLanguageMap[currentLocale] = getLanguageName(currentLocale, currentLocale);
    return localeToLanguageMap;
}, {} as Record<string, string>);
const languageToLocaleMap: Record<string, string> = Object.keys(localeToLanguageMap).reduce((languageToLocaleMap, currentLocale) => {
    languageToLocaleMap[localeToLanguageMap[currentLocale]] = currentLocale;
    return languageToLocaleMap;
}, {} as Record<string, string>);
const allLanguages = supportedLocales.map((locale) => localeToLanguageMap[locale]);

const IntlAppWithContexts = (): JSX.Element => {
    // Try reading the locale persisted from the last session. This is set in the language dropdown component
    const languageFromLocalStorage: string | undefined = localStorage.getItem(Constants.LOCAL_STORAGE_LANGUAGE_KEY);

    // Select the initial locale/language, depending on local storage, browser, and default settings
    const localeFromBrowser: string = (navigator.languages && navigator.languages[0]) || navigator.language;
    // The preferredLocale is set by the user, either through the browser or through the dropdown
    const preferredLocale: string =
        (languageFromLocalStorage && languageToLocaleMap[languageFromLocalStorage]) || // 1. The locale from the last session
        localeFromBrowser; // 2. The locale from the browser
    // supportedLocale is either preferredLocale if A2T supports it, or English
    const supportedLocale: string = isLocaleSupported(preferredLocale) ? preferredLocale : Constants.DEFAULT_LOCALE;
    const initialLanguage: string = localeToLanguageMap[supportedLocale];

    const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
    const [locale, setLocale] = useState(supportedLocale);
    const [messages, setMessages] = useState(localizedLabels[locale]);

    const setLanguage = useCallback((lang: string) => {
        const locale = languageToLocaleMap[lang];
        setLocale(locale);
        setMessages(localizedLabels[locale]);
        setSelectedLanguage(lang);
    }, []);

    return (
        <IntlProvider locale={locale} messages={messages}>
            <LocalizationContext.Provider value={{ selectedLanguage, allLanguages, locale, setLanguage }}>
                <AppWithContexts />
            </LocalizationContext.Provider>
        </IntlProvider>
    );
};

export default IntlAppWithContexts;
